import { useEffect, useRef } from 'react';

/**
 * Cloudflare Turnstile, the captcha of "Continue without an account" on the
 * agents' connection screen. Its answer is a token the API checks with
 * Cloudflare before it makes a temporary account; a token is good once, so
 * the widget is reset (`resetKey` changes) after every try.
 *
 * Cloudflare's script is loaded the first time a widget is shown: on the
 * screen that offers "Continue without an account", so the check (some
 * seconds) runs while the person reads it, not after the click.
 */

interface TurnstileApi {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      action?: string;
      theme?: 'light' | 'dark' | 'auto';
      appearance?: 'always' | 'execute' | 'interaction-only';
      callback?: (token: string) => void;
      'expired-callback'?: () => void;
      'error-callback'?: (code: string) => void;
    }
  ) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const ORIGIN = 'https://challenges.cloudflare.com';
const SCRIPT_URL = `${ORIGIN}/turnstile/v0/api.js?render=explicit`;

/**
 * Opens the connection to Cloudflare (DNS, TLS) ahead of the widget, while
 * the page still asks the API what the login is. No request is made.
 */
export const preconnectTurnstile = () => {
  if (document.querySelector(`link[rel="preconnect"][href="${ORIGIN}"]`)) {
    return;
  }
  const link = document.createElement('link');
  link.rel = 'preconnect';
  link.href = ORIGIN;
  document.head.appendChild(link);
};

let loading: Promise<TurnstileApi> | null = null;

const loadTurnstile = (): Promise<TurnstileApi> => {
  if (window.turnstile) {
    return Promise.resolve(window.turnstile);
  }
  if (!loading) {
    loading = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = SCRIPT_URL;
      script.async = true;
      script.onload = () =>
        window.turnstile ? resolve(window.turnstile) : reject(new Error('Turnstile did not load'));
      script.onerror = () => {
        loading = null;
        script.remove();
        reject(new Error('Turnstile did not load'));
      };
      document.head.appendChild(script);
    });
  }
  return loading;
};

const Turnstile = ({
  siteKey,
  action,
  resetKey,
  onToken,
  onError,
  onFail,
}: {
  siteKey: string;
  action: string;
  /** Changed to ask for a new answer: the last token was used. */
  resetKey: number;
  /** The answer, or null when it expired or failed. */
  onToken: (token: string | null) => void;
  /** The widget could not be shown (the script did not load). */
  onError: () => void;
  /**
   * The widget loaded, but Cloudflare's check failed (a blocked request, a
   * hostname the key does not allow, a browser it does not trust). The
   * widget shows nothing (interaction-only): whoever waits for an answer
   * learns it here, or waits forever.
   */
  onFail: (code: string) => void;
}) => {
  const container = useRef<HTMLDivElement>(null);
  const widget = useRef<string | null>(null);
  // The latest callbacks, without rendering the widget again for each.
  const callbacks = useRef({ onToken, onError, onFail });
  callbacks.current = { onToken, onError, onFail };

  useEffect(() => {
    let cancelled = false;
    loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !container.current) {
          return;
        }
        widget.current = turnstile.render(container.current, {
          sitekey: siteKey,
          action,
          theme: 'dark',
          // Nothing shown unless Cloudflare needs the person to do something.
          appearance: 'interaction-only',
          callback: (token) => callbacks.current.onToken(token),
          'expired-callback': () => callbacks.current.onToken(null),
          'error-callback': (code) => {
            callbacks.current.onToken(null);
            callbacks.current.onFail(code);
          },
        });
      })
      .catch(() => {
        if (!cancelled) {
          callbacks.current.onError();
        }
      });
    return () => {
      cancelled = true;
      if (widget.current && window.turnstile) {
        window.turnstile.remove(widget.current);
      }
      widget.current = null;
    };
  }, [siteKey, action]);

  useEffect(() => {
    if (resetKey && widget.current && window.turnstile) {
      callbacks.current.onToken(null);
      window.turnstile.reset(widget.current);
    }
  }, [resetKey]);

  return <div ref={container} className="flex justify-center empty:hidden" />;
};

export default Turnstile;
